import CONFIG from "Config";

import { Utils } from "@/common/utils";

import type { CreateClientConfig } from "./client/client.gen";

/**
 * Runtime configuration for the generated platform-api client.
 *
 * openapi-ts references this file via `runtimeConfigPath` in
 * `openapi-ts.config.ts`. The generated client imports `createClientConfig` and
 * calls it once on init, so the returned object becomes the client's initial
 * config. The `import type` above is erased at runtime, so the value cycle
 * between this file and the generated client is type-only.
 *
 * This client never calls platform-api's `/api/v1/auth/*` endpoints: login,
 * logout and refresh stay on the existing axios path (EAI-5675). So every 401
 * it sees means the session is gone rather than "those credentials were wrong",
 * and needs no per-endpoint exemption.
 */

/**
 * Wraps the global fetch to end a dead session the same way the legacy axios
 * path does: clear local state and send the user to login. The response is
 * still returned so the caller surfaces the error normally.
 *
 * 431 is treated like 401 on purpose. An oversized cookie header presents as a
 * rejected request rather than an auth failure, but the recovery is the same.
 *
 * Cookies ride on `credentials: "include"` and nothing else. A script cannot
 * set a `Cookie` header; browsers drop it. The platform-api session cookie is
 * established separately by `platform-session.ts`.
 */
export const platformFetch: typeof globalThis.fetch = async (input, init) => {
    const response = await fetch(input, init);

    if (response.status === 401 || response.status === 431) {
        Utils.clearLocalstorage();
        window.location.replace((CONFIG.paths?.base ?? "/") + "login");
    }

    return response;
};

/**
 * Called by the generated client on initialization. Points it at platform-api,
 * sends the session cookies, and installs the session handling above.
 *
 * No custom headers, deliberately. `EAIClient` sends `x-ui-location`, and
 * platform-api does read it (`AuditResponseFilter`), but its CORS policy only
 * allows `accept,authorization,content-type,x-requested-with`, so a browser
 * sending it fails preflight. Omitting it also keeps GETs as simple requests,
 * with no preflight round trip at all. Platform UI sends no custom headers
 * either. Restore this once platform-api's `quarkus.http.cors.headers`
 * includes it.
 */
export const createClientConfig: CreateClientConfig = override => ({
    ...override,
    baseUrl: CONFIG.apis?.newApi ?? "",
    credentials: "include",
    fetch: platformFetch,
});
